import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../../context/PlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import AddBuyInDialog from '../AddBuyInDialog/AddBuyInDialog';
import './PlayerTable.scss';

const PlayerTable = () => {
  const navigate = useNavigate();
  const { getFilteredPlayers, deletePlayer, searchTerm } = usePlayer();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [addBuyInOpen, setAddBuyInOpen] = useState(false);
  const [addBuyInPlayerId, setAddBuyInPlayerId] = useState(null);

  const players = getFilteredPlayers();

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuOpen = (event, player) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPlayer(player);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPlayer(null);
  };

  const handleDeletePlayer = () => {
    if (selectedPlayer && window.confirm(`Are you sure you want to delete ${selectedPlayer.name}?`)) {
      deletePlayer(selectedPlayer.id);
    }
    handleMenuClose();
  };


  const handleViewPlayer = (playerId) => {
    navigate(`player/${playerId}`);
  };

  const handleAddBuyIn = (playerId) => {
    setAddBuyInPlayerId(playerId);
    setAddBuyInOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getBuyInCount = (player) => {
    return player.buyInHistory ? player.buyInHistory.length : 0;
  };

  const sortedPlayers = React.useMemo(() => {
    return [...players].sort((a, b) => {
      let aValue, bValue;
      
      switch (orderBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'totalBuyIn':
          aValue = a.totalBuyIn;
          bValue = b.totalBuyIn;
          break;
        case 'buyInCount':
          aValue = getBuyInCount(a);
          bValue = getBuyInCount(b);
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [players, order, orderBy]);

  if (players.length === 0) {
    return (
      <Box className="empty-state">
        <div className="empty-icon">♠</div>
        <Typography variant="h5" className="empty-title">
          {searchTerm ? 'No players found' : 'No players yet'}
        </Typography>
        <Typography variant="body1" color="text.secondary" className="empty-subtitle">
          {searchTerm 
            ? `Try searching for a different name` 
            : 'Start by adding your first player to track buy-ins'
          }
        </Typography>
      </Box>
    );
  }

  const headCells = [
    { id: 'name', label: 'Player' },
    { id: 'totalBuyIn', label: 'Total Buy-in', align: 'right' },
    { id: 'buyInCount', label: 'No of Buy-Ins', align: 'center' },
    { id: 'actions', label: 'Actions', align: 'center', sortable: false }
  ];

  return (
    <>
      <TableContainer component={Paper} className="player-table">
        <Table>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.align || 'left'}
                  className="table-header-cell"
                >
                  {headCell.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                      className="sort-label"
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPlayers.map((player) => {
              const playerInitials = getInitials(player.name);
              const backgroundColor = player.profileColor || "#006aff";
              const textColor = getContrastTextColor(backgroundColor);
              
              return (
                <TableRow 
                  key={player.id} 
                  className="table-row"
                  hover
                  onClick={() => handleViewPlayer(player.id)}
                >
                  <TableCell className="player-cell">
                    <Box className="player-info">
                      <Avatar 
                        className="player-avatar"
                        sx={{
                          backgroundColor: backgroundColor,
                          color: textColor,
                          fontWeight: 700
                        }}
                      >
                        {playerInitials}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" className="player-name">
                          {player.name}
                        </Typography>
                        {player.totalBuyIn > 0 && (
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                            className="status-chip"
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right" className="amount-cell">
                    <Typography variant="body1" className="total-amount">
                      {formatCurrency(player.totalBuyIn)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={getBuyInCount(player)} 
                      size="small" 
                      variant="outlined"
                      className="count-chip"
                    />
                  </TableCell>
                  <TableCell align="center" className="actions-cell">
                    <Box className="action-buttons">
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPlayer(player.id);
                        }}
                        className="view-button desktop-only"
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddBuyIn(player.id);
                        }}
                        className="add-button desktop-only"
                      >
                        Add Buy-in
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, player)}
                        className="menu-button"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleViewPlayer(selectedPlayer?.id);
          handleMenuClose();
        }}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          handleAddBuyIn(selectedPlayer?.id);
          handleMenuClose();
        }}>
          <AddIcon sx={{ mr: 1 }} />
          Add Buy-in
        </MenuItem>
        <MenuItem onClick={handleDeletePlayer} sx={{ color: 'error.main' }}>
          Delete Player
        </MenuItem>
      </Menu>


      <AddBuyInDialog
        open={addBuyInOpen}
        onClose={() => {
          setAddBuyInOpen(false);
          setAddBuyInPlayerId(null);
        }}
        playerId={addBuyInPlayerId}
      />
    </>
  );
};

export default PlayerTable;